import React, { useState, useEffect, useMemo } from "react";
import { useSelector, useDispatch } from "react-redux";
import { IStore } from "@/redux/defaultStore";
import { resetModalCount } from "@/redux/meta/metaActions";
import { useUserQuestStats } from "@/hooks/useUserQuestStats";
import styles from "./RotatingActionCards.module.scss";

// Individual action card components
import SpinTheWheelCard from "./actions/SpinTheWheelCard";
import InviteShareCard from "./actions/InviteShareCard";
import TodayQuestProgressCard from "./actions/TodayQuestProgressCard";
import FuelUpProgressCard from "./actions/FuelUpProgressCard";
import SocialShareCard from "./actions/SocialShareCard";
import LootBoxCard from "./actions/LootBoxCard";

export interface ActionCard {
    id: string;
    priority: number;
    component: React.FC<any>;
    isAvailable: boolean;
    data?: any;
}

/**
 * RotatingActionCards - Dynamic action cards that rotate based on availability and priority
 * Displays two cards at a time in the hero section, cycling through available actions
 */
const RotatingActionCards: React.FC = () => {
    const [currentIndex, setCurrentIndex] = useState(0);
    const [hasSpunWheel, setHasSpunWheel] = useState(false);
    const [modalOpen, setModalOpen] = useState(false);
    const dispatch = useDispatch();

    // Get user data for determining card availability
    const currentUser = useSelector((store: IStore) => store.metaStore.currentUser);
    const fuelBalance = useSelector((store: IStore) => store.metaStore.fuelBalance);
    const { summary } = useUserQuestStats();

    // Check for daily resets based on user data
    useEffect(() => {
    // Spin availability is handled inside SpinTheWheelCard component
    }, []);

    // Define all available cards with their priority and availability
    const availableCards = useMemo<ActionCard[]>(() => {
        const cards: ActionCard[] = [];

        // Priority 1: Spin the Wheel (daily - component handles its own availability)
        cards.push({
            id: "spin-wheel",
            priority: 1,
            component: SpinTheWheelCard,
            isAvailable: true, // Component will return null if not available
            data: {
                onSpin: () => {
                    setHasSpunWheel(true);
                },
                onModalOpen: () => setModalOpen(true),
                onModalClose: () => {
                    setModalOpen(false);
                    // Fallback: ensure scroll is restored if modal count gets out of sync
                    setTimeout(() => {
                        if (document.body.style.overflow === "hidden") {
                            document.body.style.overflow = "";
                            document.body.style.paddingRight = "0";
                        }
                    }, 100);
                },
            },
        });

        // Priority 2: Invite Share (always available - users can invite multiple friends)
        cards.push({
            id: "invite-share",
            priority: 2,
            component: InviteShareCard,
            isAvailable: true,
            data: {
                onModalOpen: () => setModalOpen(true),
                onModalClose: () => setModalOpen(false),
            },
        });

        // Priority 2.5: LootBox - Major Revenue Driver (always available)
        cards.push({
            id: "lootbox",
            priority: 2.5,
            component: LootBoxCard,
            isAvailable: true,
            data: {
                onModalOpen: () => setModalOpen(true),
                onModalClose: () => setModalOpen(false),
            },
        });

        // Priority 3: Today's Quest Progress (if active quests)
        if (summary.activeCount > 0) {
            cards.push({
                id: "quest-progress",
                priority: 3,
                component: TodayQuestProgressCard,
                isAvailable: true,
                data: {
                    activeQuests: summary.activeQuests,
                    overallProgress: summary.overallProgress,
                },
            });
        }

        // Priority 4: Fuel-Up Progress (always available)
        cards.push({
            id: "fuel-up",
            priority: 4,
            component: FuelUpProgressCard,
            isAvailable: true,
            data: {
                currentFuel: fuelBalance || "0",
                nextReward: "2X XP Boost",
            },
        });

        // Priority 5: Social Share (fallback)
        cards.push({
            id: "social-share",
            priority: 5,
            component: SocialShareCard,
            isAvailable: true,
            data: {
                xpReward: 100,
                onModalOpen: () => setModalOpen(true),
                onModalClose: () => {
                    setModalOpen(false);
                    // Fallback: ensure scroll is restored if modal count gets out of sync
                    setTimeout(() => {
                        if (document.body.style.overflow === "hidden") {
                            document.body.style.overflow = "";
                            document.body.style.paddingRight = "0";
                        }
                    }, 100);
                },
            },
        });

        // Sort by priority
        return cards.sort((a, b) => a.priority - b.priority);
    }, [hasSpunWheel, summary, fuelBalance]);

    /*
     * Auto-rotate cards every 10 seconds (was 30 seconds - too slow for users)
     * Pause rotation when any modal is open
     */
    useEffect(() => {
        if (availableCards.length <= 2 || modalOpen) return;

        const interval = setInterval(() => {
            setCurrentIndex((prev) => (prev + 2) % availableCards.length);
        }, 10000);

        return () => clearInterval(interval);
    }, [availableCards.length, modalOpen]);

    /*
     * Reset modal count on mount to prevent scroll lock issues
     * This fixes cases where modal count gets out of sync due to component unmounting
     */
    useEffect(() => {
    // Only reset if we're not currently showing a modal
        if (!modalOpen) {
            dispatch(resetModalCount());
        }
    }, []);

    // Cleanup modal count if component unmounts while modal is open
    useEffect(() => {
        return () => {
            if (modalOpen) {
                dispatch(resetModalCount());
            }
        };
    }, [modalOpen]);

    // Get the two cards to display
    const displayCards = useMemo(() => {
        const cards = [];
        const firstCard = availableCards[currentIndex];
        const secondCard = availableCards[(currentIndex + 1) % availableCards.length];

        if (firstCard) cards.push(firstCard);
        if (secondCard && availableCards.length > 1) cards.push(secondCard);

        return cards;
    }, [availableCards, currentIndex]);

    // Handle manual rotation
    const handleRotate = () => {
        setCurrentIndex((prev) => (prev + 2) % availableCards.length);
    };

    return (
        <>
            {displayCards.map((card, index) => {
                const CardComponent = card.component;
                return <CardComponent key={`${card.id}-${index}`} {...card.data} />;
            })}
        </>
    );
};

export default RotatingActionCards;